"use client";

/**
 * Pricing tier cards with a monthly / annual billing toggle.
 *
 * Client component because the toggle is interactive state; everything it
 * renders comes from the display-only `PRICING_PLANS` config. No billing,
 * entitlement or permission logic lives here — CTAs route to trial signup, a
 * guided pilot or contact (FixControl has no self-serve checkout).
 *
 * Visual grammar matches the rest of the marketing surface (design-system
 * tokens, <Reveal> scroll-fade). The Team tier is visually promoted as the
 * recommended plan.
 */

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Check, Users, Zap } from "lucide-react";
import { Reveal } from "@/components/reveal";
import { useT } from "@/i18n";
import type { T } from "@/i18n/translate";
import {
  PRICING_PLANS,
  formatEuro,
  type BillingPeriod,
  type PlanId,
  type PricingPlan,
} from "@/lib/marketing-pricing";

/** PlanId → its name i18n key, so an `inheritsFrom` id resolves to a plan name. */
const PLAN_NAME_KEY: Record<PlanId, string> = Object.fromEntries(
  PRICING_PLANS.map((p) => [p.id, p.name]),
) as Record<PlanId, string>;

export function PricingTiers() {
  const t = useT();
  const [period, setPeriod] = useState<BillingPeriod>("monthly");

  return (
    <div>
      <Reveal>
        <BillingToggle period={period} onChange={setPeriod} t={t} />
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {PRICING_PLANS.map((plan, i) => (
          <Reveal key={plan.id} delay={i * 70}>
            <PlanCard plan={plan} period={period} t={t} />
          </Reveal>
        ))}
      </div>

      <p className="mt-8 text-center text-[12px] text-fg-muted">
        {t("pricingPage.tiers.allPricesNote")}
      </p>
      <p className="mt-2 text-center text-[12px] text-fg-muted">
        {t("pricingPage.tiers.enforcementNote")}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function BillingToggle({
  period,
  onChange,
  t,
}: {
  period: BillingPeriod;
  onChange: (p: BillingPeriod) => void;
  t: T;
}) {
  return (
    <div className="flex items-center justify-center">
      <div
        role="tablist"
        aria-label="Billing period"
        className="inline-flex items-center gap-1 rounded-full border border-line-subtle bg-bg-raised p-1"
      >
        {(["monthly", "annual"] as const).map((p) => {
          const active = period === p;
          return (
            <button
              key={p}
              role="tab"
              aria-selected={active}
              onClick={() => onChange(p)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-medium transition ${
                active
                  ? "bg-brand-grad text-white shadow-glow"
                  : "text-fg-dim hover:text-fg-bright"
              }`}
            >
              {p === "monthly" ? t("pricingPage.tiers.monthly") : t("pricingPage.tiers.annual")}
              {p === "annual" && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    active
                      ? "bg-white/20 text-white"
                      : "bg-status-ok/10 text-status-ok"
                  }`}
                >
                  {t("pricingPage.tiers.twoMonthsFree")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function PlanCard({ plan, period, t }: { plan: PricingPlan; period: BillingPeriod; t: T }) {
  const recommended = !!plan.recommended;
  return (
    <div
      className={`relative flex h-full flex-col rounded-2xl border bg-bg-raised p-6 ${
        recommended
          ? "border-brand-500/40 shadow-[0_30px_90px_-30px_rgba(244,63,142,0.45)]"
          : "border-line-subtle"
      }`}
    >
      {recommended && (
        <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-brand-grad px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-glow">
          {t("pricingPage.tiers.recommended")}
        </span>
      )}

      <div className="text-[17px] font-semibold text-fg-bright">{t(plan.name)}</div>
      <p className="mt-1.5 text-[13px] text-fg-dim leading-relaxed min-h-[40px]">
        {t(plan.positioning)}
      </p>

      <PriceBlock plan={plan} period={period} t={t} />

      <Link
        href={plan.cta.href}
        className={`mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[14px] font-semibold transition ${
          recommended
            ? "bg-brand-grad text-white shadow-glow hover:brightness-110"
            : "border border-line bg-bg-elev text-fg hover:bg-bg-hover hover:border-line-strong"
        }`}
      >
        {t(plan.cta.label)}
        <ArrowRight size={15} />
      </Link>
      {plan.ctaNote && (
        <p className="mt-2 text-[11px] text-fg-muted leading-relaxed text-center">
          {t(plan.ctaNote)}
        </p>
      )}

      <div className="mt-5 space-y-1.5 rounded-xl border border-line-subtle bg-bg-elev px-4 py-3">
        <div className="flex items-center gap-2 text-[12px] text-fg-dim">
          <Users size={13} className="text-brand-300 flex-shrink-0" />
          {t(plan.limits.users)}
        </div>
        <div className="flex items-center gap-2 text-[12px] text-fg-dim">
          <Zap size={13} className="text-brand-300 flex-shrink-0" />
          {t(plan.limits.aiActions)}
        </div>
      </div>

      <div className="mt-5">
        {plan.inheritsFrom && (
          <div className="mb-2 text-[12px] font-medium text-fg-bright">
            {t("pricingPage.tiers.everythingIn", { plan: t(PLAN_NAME_KEY[plan.inheritsFrom]) })}
          </div>
        )}
        <ul className="space-y-2">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[13px] text-fg-dim leading-relaxed">
              <Check size={14} className="mt-0.5 flex-shrink-0 text-brand-300" />
              <span>{t(f)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 border-t border-line-subtle pt-4 text-[12px] text-fg-muted leading-relaxed">
        <span className="font-medium text-fg-dim">{t("pricingPage.tiers.bestFor")}</span>
        {t(plan.forWho)}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function PriceBlock({ plan, period, t }: { plan: PricingPlan; period: BillingPeriod; t: T }) {
  // Enterprise (no annual figure): always show the "From €x /mo" entry point.
  const showAnnual = period === "annual" && plan.annual != null;

  if (plan.monthly == null) {
    return (
      <div className="mt-5">
        <div className="text-[32px] font-semibold leading-none text-fg-bright">
          {t("pricingPage.tiers.custom")}
        </div>
        <div className="mt-2 text-[12px] text-fg-muted">
          {t("pricingPage.tiers.customTailored")}
        </div>
      </div>
    );
  }

  if (showAnnual && plan.annual != null) {
    const perMonth = Math.round(plan.annual / 12);
    return (
      <div className="mt-5">
        <div className="flex items-baseline gap-1.5">
          {plan.pricePrefix && (
            <span className="text-[14px] font-medium text-fg-dim">{t(plan.pricePrefix)}</span>
          )}
          <span className="text-[36px] font-semibold leading-none text-fg-bright">
            {formatEuro(plan.annual)}
          </span>
          <span className="text-[14px] text-fg-muted">{t("pricingPage.tiers.perYear")}</span>
        </div>
        <div className="mt-2 text-[12px] text-fg-muted">
          {t("pricingPage.tiers.annualHint", { amount: formatEuro(perMonth) })}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="flex items-baseline gap-1.5">
        {plan.pricePrefix && (
          <span className="text-[14px] font-medium text-fg-dim">{t(plan.pricePrefix)}</span>
        )}
        <span className="text-[36px] font-semibold leading-none text-fg-bright">
          {formatEuro(plan.monthly)}
        </span>
        <span className="text-[14px] text-fg-muted">{t("pricingPage.tiers.perMonth")}</span>
      </div>
      <div className="mt-2 text-[12px] text-fg-muted">
        {plan.annual != null
          ? t("pricingPage.tiers.orPerYear", { amount: formatEuro(plan.annual) })
          : t("pricingPage.tiers.customAnnual")}
      </div>
    </div>
  );
}
